import { ReactNode, useId, useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export const record = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
export const humanize = (value: string) => {
  const text = value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return (text.charAt(0).toUpperCase() + text.slice(1)).replace(
    /\bid\b/gi,
    'ID'
  );
};
export const duration = (value?: number | null) =>
  value == null
    ? 'Unavailable'
    : value < 1000
    ? `${value} ms`
    : `${(value / 1000).toFixed(2)} s`;
export const timestamp = (value?: string, timezone?: string) => {
  if (!value || !Number.isFinite(new Date(value).getTime()))
    return value || 'Unavailable';
  try {
    return new Date(value).toLocaleString(undefined, {
      timeZone: timezone,
      timeZoneName: 'short',
    });
  } catch {
    return new Date(value).toLocaleString(undefined, { timeZoneName: 'short' });
  }
};

export function StatusBadge({ status }: { status?: string }) {
  const colors: Record<string, string> = {
    running: '#1565c0',
    waiting: 'warning.dark',
    paused: 'warning.dark',
    uncertain: 'warning.dark',
    completed: 'success.main',
    failed: 'error.main',
    timed_out: 'error.main',
    unavailable: 'error.main',
  };
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        color: colors[status || ''] || 'text.secondary',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {status ? humanize(status) : 'No runs'}
    </Typography>
  );
}

export function Disclosure({
  label,
  children,
  defaultOpen = false,
}: {
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Button
        size="small"
        color="inherit"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        startIcon={open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        onClick={() => setOpen(!open)}
        sx={{
          justifyContent: 'flex-start',
          textAlign: 'left',
          overflowWrap: 'anywhere',
          maxWidth: '100%',
        }}
      >
        {label}
      </Button>
      {open && (
        <Box id={id} sx={{ mt: 1, mb: 2 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

export function LongText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const long = text.length > 600;
  return (
    <Box>
      <Typography
        component="div"
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          lineHeight: 1.7,
        }}
      >
        {long && !expanded
          ? `${text.slice(0, 600)}…`
          : text || '(empty string)'}
      </Typography>
      {long && (
        <Button size="small" onClick={() => setExpanded(!expanded)}>
          {expanded
            ? 'Show less text'
            : `Show full text (${text.length.toLocaleString()} characters)`}
        </Button>
      )}
    </Box>
  );
}

export interface FormatMetadata {
  currency?: string;
  moneyUnit?: string;
  timezone?: string;
  timeOffsetUnit?: string;
}

function formatted(
  value: unknown,
  key: string,
  metadata: FormatMetadata
): string {
  if (value === null) return 'null';
  if (value === undefined) return 'Not recorded';
  if (
    typeof value === 'number' &&
    metadata.currency &&
    metadata.moneyUnit === 'cents' &&
    /^(amount|price|total|subtotal|balance|tax|fee|tip|deposit)(_|$)/.test(key)
  ) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: metadata.currency,
      }).format(value / 100);
    } catch {
      /* Preserve the original value if metadata is unfamiliar. */
    }
  }
  if (
    typeof value === 'number' &&
    /_offset$/.test(key) &&
    metadata.timeOffsetUnit === 'minutes after local midnight'
  ) {
    const hours = Math.floor(value / 60);
    return `${String(((hours % 24) + 24) % 24).padStart(2, '0')}:${String(
      ((value % 60) + 60) % 60
    ).padStart(2, '0')}${
      hours >= 24 ? ` (+${Math.floor(hours / 24)} days)` : ''
    }${metadata.timezone ? ` · ${metadata.timezone}` : ''}`;
  }
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T/.test(value) &&
    /(_at|At)$/.test(key)
  )
    return timestamp(value, metadata.timezone);
  return String(value);
}

function Value({
  value,
  field,
  metadata,
}: {
  value: unknown;
  field: string;
  metadata: FormatMetadata;
}) {
  if (Array.isArray(value) || record(value)) {
    const size = Array.isArray(value)
      ? value.length
      : Object.keys(value as object).length;
    if (!size)
      return (
        <Typography variant="body2">
          {Array.isArray(value) ? '[] (empty list)' : '{} (empty object)'}
        </Typography>
      );
    return (
      <Disclosure
        label={`${size} ${
          Array.isArray(value)
            ? size === 1
              ? 'item'
              : 'items'
            : size === 1
            ? 'field'
            : 'fields'
        }`}
      >
        <DataView value={value} metadata={metadata} />
      </Disclosure>
    );
  }
  return <LongText text={formatted(value, field, metadata)} />;
}

export function DataView({
  value,
  metadata = {},
}: {
  value: unknown;
  metadata?: FormatMetadata;
}) {
  const [limit, setLimit] = useState(20);
  const object = record(value);
  if (!object && !Array.isArray(value))
    return <Value value={value} field="" metadata={metadata} />;
  const count = Array.isArray(value)
    ? value.length
    : Object.keys(object!).length;
  const entries = Array.isArray(value)
    ? value
        .slice(0, limit)
        .map((item, index) => [String(index + 1), item] as const)
    : Object.entries(object!).slice(0, limit);
  if (!count) return <Value value={value} field="" metadata={metadata} />;
  return (
    <Box>
      <Box component="dl" sx={{ m: 0 }}>
        {entries.map(([key, item]) => (
          <Box
            key={key}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'minmax(120px, 180px) minmax(0, 1fr)',
              },
              gap: 1,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              component="dt"
              variant="body2"
              color="text.secondary"
              sx={{ overflowWrap: 'anywhere' }}
            >
              {Array.isArray(value) ? `Item ${key}` : humanize(key)}
            </Typography>
            <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
              <Value value={item} field={key} metadata={metadata} />
            </Box>
          </Box>
        ))}
      </Box>
      {limit < count && (
        <Button size="small" onClick={() => setLimit(limit + 20)}>
          Show more ({count - limit} remaining)
        </Button>
      )}
    </Box>
  );
}

function JsonBody({ value }: { value: unknown }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        bgcolor: 'grey.100',
        borderRadius: 1,
        maxHeight: 420,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        fontSize: 12,
      }}
    >
      {JSON.stringify(value, null, 2) ?? 'Not recorded'}
    </Box>
  );
}
export function RawJson({ value }: { value: unknown }) {
  const [notice, setNotice] = useState<{ error: boolean; text: string } | null>(
    null
  );
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        flexWrap="wrap"
      >
        <Button
          size="small"
          startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(
                JSON.stringify(value, null, 2) ?? 'null'
              );
              setNotice({ error: false, text: 'Copied JSON.' });
            } catch {
              setNotice({
                error: true,
                text: 'Unable to copy JSON. Open Raw JSON to copy it manually.',
              });
            }
          }}
        >
          Copy JSON
        </Button>
      </Stack>
      <Disclosure label="Raw JSON">
        <JsonBody value={value} />
      </Disclosure>
      {notice && (
        <Alert
          severity={notice.error ? 'error' : 'success'}
          onClose={() => setNotice(null)}
          sx={{ mt: 1 }}
        >
          {notice.text}
        </Alert>
      )}
    </Box>
  );
}
